import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getSpots, isBasicConfigured } from "../api/protected";
import { syncAllPosterSpotsToDb } from "../api/sync";
import { asArray } from "../poster/response";
import {
  getAccountUrl,
  getPosterToken,
  setCachedPosterUser,
  setPosterToken,
} from "../session";
import { toast } from "react-toastify";
import { MOCK_USER } from "../mocks";
import { InlineLoader, PageLoader } from "../components/Loader";

const IS_DEV = process.env.REACT_APP_DEV === "true";

function SpotLaptopIcon() {
  return (
    <svg
      className="spotCard__tabletsIcon"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect
        x="3"
        y="4"
        width="18"
        height="12"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M2 18h20"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function SpotsPage() {
  const accountUrl = getAccountUrl();
  const [items, setItems] = useState(/** @type {any[]} */ ([]));
  const [load, setLoad] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [backgroundSync, setBackgroundSync] = useState(false);

  const loadList = useCallback(async () => {
    if (!isBasicConfigured() || !String(accountUrl || "").trim()) {
      setItems([]);
      return;
    }
    const raw = await getSpots(accountUrl);
    setItems(asArray(raw));
  }, [accountUrl]);

  const syncPosterThenRefresh = useCallback(
    async (/** @type {string} */ tok) => {
      if (!isBasicConfigured() || !String(accountUrl || "").trim() || !tok) {
        return;
      }
      const updated = await syncAllPosterSpotsToDb(tok, accountUrl);
      setItems(asArray(updated));
    },
    [accountUrl],
  );

  useEffect(() => {
    let cancelled = false;
    if (IS_DEV && !getPosterToken() && MOCK_USER?.access_token) {
      setPosterToken(String(MOCK_USER.access_token));
      setCachedPosterUser(MOCK_USER);
    }
    (async () => {
      setLoad(true);
      const tok = getPosterToken();
      if (!tok) {
        if (!cancelled) {
          setLoad(false);
        }
        return;
      }
      const acc = (getAccountUrl() || "").trim();
      if (!isBasicConfigured() || !acc) {
        if (!cancelled) {
          setItems([]);
          setLoad(false);
        }
        if (!IS_DEV) {
          toast.error(
            "Нужны Basic (REACT_APP_…) и account_url, чтобы загрузить точки из API",
          );
        }
        return;
      }
      try {
        try {
          await loadList();
        } catch (e) {
          console.error(e);
          if (!cancelled) {
            toast.error("Не удалось загрузить точки из API");
            setItems([]);
          }
        } finally {
          if (!cancelled) {
            setLoad(false);
          }
        }
        if (cancelled) {
          return;
        }
        if (!isBasicConfigured() || !acc) {
          return;
        }
        // Синхронизация с Poster после отрисовки ответа GET
        setTimeout(() => {
          if (cancelled) {
            return;
          }
          (async () => {
            setBackgroundSync(true);
            try {
              const t2 = getPosterToken();
              if (t2) {
                const updated = await syncAllPosterSpotsToDb(t2, acc);
                if (!cancelled) {
                  setItems(asArray(updated));
                }
              }
            } catch (e) {
              console.warn("фон: синк Poster → API", e);
              if (!cancelled) {
                try {
                  await loadList();
                } catch {
                  /* */
                }
              }
            } finally {
              if (!cancelled) {
                setBackgroundSync(false);
              }
            }
          })();
        }, 0);
      } catch {
        if (!cancelled) {
          setLoad(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountUrl, loadList]);

  const handleSync = async () => {
    const tok = getPosterToken();
    if (!tok) {
      toast.error("Нет сессии Poster");
      return;
    }
    if (!isBasicConfigured() || !String(accountUrl || "").trim()) {
      toast.error("Настройте Basic Auth и account_url");
      return;
    }
    setSyncing(true);
    try {
      try {
        await syncPosterThenRefresh(tok);
        toast.success("Синхронизировано");
      } catch (e) {
        console.error(e);
        toast.error("Синхронизация не удалась");
        try {
          await loadList();
        } catch {
          /* */
        }
      }
    } finally {
      setSyncing(false);
    }
  };

  if (!getPosterToken() && !IS_DEV) {
    return (
      <section className="bodyContainer bodyContainer--centered-wide">
        <h2>Заведения</h2>
        <p className="text-muted">Нет токена Poster. Пройдите OAuth.</p>
      </section>
    );
  }

  if (load) {
    return (
      <section className="bodyContainer bodyContainer--centered-wide">
        <PageLoader />
      </section>
    );
  }

  const canUseApi = isBasicConfigured() && String(accountUrl || "").trim();

  return (
    <section className="bodyContainer bodyContainer--centered-wide">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <h2 className="settingsPage__title m-0">Заведения</h2>
        <button
          type="button"
          className="link-button link-button--sm d-inline-flex align-items-center justify-content-center"
          style={{ minWidth: "11rem" }}
          onClick={handleSync}
          disabled={syncing || !canUseApi}
        >
          {syncing ? <InlineLoader label="Синхронизация" /> : "Синхронизировать"}
        </button>
      </div>
      {backgroundSync && canUseApi && (
        <div
          className="d-flex align-items-center mb-2"
          role="status"
          aria-label="Синхронизация с Poster"
        >
          <span className="appSpinner appSpinner--subtle" aria-hidden />
        </div>
      )}
      {!canUseApi && (
        <p className="text-warning small">
          Без Basic Auth и <code>account_url</code> в сессии точки из вашего
          API недоступны.
        </p>
      )}
      <ul className="list-unstyled spotGrid">
        {items.length === 0 && canUseApi && (
          <li className="text-muted">
            Пока нет точек в API. Синхронизируйте с Poster (кнопка выше или
            дождитесь фона).
          </li>
        )}
        {items.map((s) => {
          const sp = /** @type {any} */ (s);
          const id = sp.spot_id;
          const clientName =
            sp.client_id &&
            typeof sp.client_id === "object" &&
            /** @type {any} */(sp.client_id).name
              ? String(/** @type {any} */ (sp.client_id).name)
              : null;
          const addr = sp.address != null ? String(sp.address) : "";
          const tablets = sp.tablets;
          const tabletCount = Array.isArray(tablets) ? tablets.length : 0;
          return (
            <li key={String(sp._id ?? id)} className="spotCard">
              <div className="spotCard__header">
                <div className="spotCard__nameBlock">
                  <div className="spotCard__name">{sp.name || "—"}</div>
                </div>
                <div
                  className="spotCard__tabletsStat"
                  title={`Касс: ${tabletCount}`}
                  aria-label={`Касс в заведении: ${tabletCount}`}
                >
                  <span className="spotCard__tabletsCount">{tabletCount}</span>
                  <SpotLaptopIcon />
                </div>
              </div>
              {clientName && (
                <div className="spotCard__addr text-muted small">
                  {clientName}
                </div>
              )}
              {addr && (
                <div className="spotCard__addr text-muted small mt-1">
                  {addr}
                </div>
              )}
              <div className="text-muted small mt-1">ID: {String(id)}</div>
              <Link
                className="link-button link-button--sm link-button--secondary mt-2"
                to={`/spots/${encodeURIComponent(String(id))}`}
              >
                Кассы
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
